import { useMutation, useQuery } from 'react-query';
import ContractsController from '../../../API/TT_ELD/contracts';

export interface ContractPrice {
  id: string;
  currency: string;
  product: string;
  type: string;
  price: number;
}

export interface ContractPriceListResponse {
  data: ContractPrice[];
}
interface CardInfo {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

interface BankAccountInfo {
  bank_name: string;
  last4: string;
}

export interface PaymentMethod {
  id: string;
  type: string;
  card?: CardInfo;
  us_bank_account?: BankAccountInfo;
  isDefault?: boolean;
}

interface PaymentMethodResponse {
  data: PaymentMethod[];
}

export function useContracts(
  page: number = 1,
  status?: string,
  companyId?: string,
  type?: string,
  isDashcam?: boolean,
  companyContractStatus?: string,
) {
  return useQuery({
    queryKey: ['contracts', page, status, companyId, type, isDashcam, companyContractStatus],
    queryFn: () =>
      ContractsController.list(page, status, companyId, type, isDashcam, companyContractStatus),
    refetchOnWindowFocus: false,
    enabled: !!status || !!companyContractStatus,
  });
}

export function useContract(contractID?: string, includePendingCompanyContract?: boolean) {
  return useQuery({
    queryKey: ['contract', contractID, includePendingCompanyContract],
    queryFn: () => ContractsController.find(contractID!, includePendingCompanyContract),
    enabled: !!contractID,
  });
}
export function useContractPriceList(contractID: string) {
  return useQuery<ContractPriceListResponse>({
    queryKey: ['price-list', contractID],
    queryFn: () => ContractsController.pricesList(contractID),
    enabled: !!contractID,
  });
}
export function useCreateContractPriceList(companyId: string) {
  return useQuery<ContractPriceListResponse>({
    queryKey: ['create-price-list', companyId],
    queryFn: () => ContractsController.createPricesList(companyId),
    enabled: !!companyId,
  });
}
export function usePaymentMethodsList(contractID: string) {
  return useQuery<PaymentMethodResponse>({
    queryKey: ['payment-method', contractID],
    queryFn: () => ContractsController.paymentMethod(contractID),
    enabled: !!contractID,
  });
}
export function useCreatePaymentMethodsList(companyId: string) {
  return useQuery<PaymentMethodResponse>({
    queryKey: ['create-payment-method', companyId],
    queryFn: () => ContractsController.createPaymentMethod(companyId),
    enabled: !!companyId,
  });
}

export function useEditContract() {
  return useMutation(({ contractId, payload }: { contractId: string; payload: any }) =>
    ContractsController.edit(contractId, payload),
  );
}
export function useCreateContract() {
  return useMutation(({ companyId, payload }: { companyId: string; payload: any }) =>
    ContractsController.create(companyId, payload),
  );
}

export function useUploadDocument() {
  return useMutation((file: File) => ContractsController.uploadDocument(file));
}

export function useUpdateStatus() {
  return useMutation(({ contractId, payload }: { contractId: string; payload: any }) =>
    ContractsController.updateStatus(contractId, payload),
  );
}

export function useDeleteContract() {
  return useMutation((contractId: string) => ContractsController.deleteContract(contractId));
}

export function useCreateDashCamRequest() {
  return useMutation(({ companyId, payload }: { companyId: string; payload: any }) =>
    ContractsController.createDashCamRequest(companyId, payload),
  );
}
export function useEditDashCamRequest() {
  return useMutation(({ contractId, payload }: { contractId: string; payload: any }) =>
    ContractsController.editDashCamRequest(contractId, payload),
  );
}

export function useEditCompanyContract() {
  return useMutation(({ contractId, payload }: { contractId: string; payload: any }) =>
    ContractsController.editCompanyContract(contractId, payload),
  );
}
